/**
 * Class-level display helpers (SDA-269).
 *
 * The age range is a fixed pairing with the class level — the studio sees it
 * when it books (TimeSlotSelector) and the faculty/studio class-prep emails
 * repeat it (workers `email.ts:formatClassLevelLine`). Admin needs the same
 * copy on the booking detail page, so keep this mapping in sync with those.
 *
 * Legacy `type_a`/`type_b`/`type_c` keys are accepted defensively: the
 * `classes.class_level` enum holds none of them today, but older UI copy and
 * the public booking flow still speak in Type A/B/C.
 */

export interface ClassLevelDisplay {
  /** Human label for the level, or '—' when the level is unknown/absent. */
  label: string;
  /** Age range that pairs with the level, or null when there isn't one. */
  ages: string | null;
}

const LEVEL_DISPLAY: Record<string, ClassLevelDisplay> = {
  beginner: { label: 'Beginner', ages: 'Ages 9+' },
  type_a: { label: 'Beginner', ages: 'Ages 9+' },
  intermediate: { label: 'Intermediate', ages: 'Ages 9-12' },
  type_b: { label: 'Intermediate', ages: 'Ages 9-12' },
  advanced: { label: 'Int / Advanced', ages: 'Ages 12+' },
  type_c: { label: 'Int / Advanced', ages: 'Ages 12+' },
  all_levels: { label: 'All Levels', ages: null },
};

/** The three levels an admin can pick when creating a class (SDA-274). */
export type SelectableClassLevel = 'beginner' | 'intermediate' | 'advanced';

export interface ClassLevelPickerOption {
  value: SelectableClassLevel;
  label: string;
  ages: string;
}

/**
 * Options for the admin manual-booking level picker. `all_levels` is
 * deliberately absent — the admin must commit to one of the three real levels.
 * Copy is duplicated from LEVEL_DISPLAY above so the picker can render without
 * a lookup; classLevel.test.ts asserts the two stay in sync.
 */
export const CLASS_LEVEL_PICKER_OPTIONS: ClassLevelPickerOption[] = [
  { value: 'beginner', label: 'Beginner', ages: 'Ages 9+' },
  { value: 'intermediate', label: 'Intermediate', ages: 'Ages 9-12' },
  { value: 'advanced', label: 'Int / Advanced', ages: 'Ages 12+' },
];

/**
 * Resolve a stored class level into its display label and age range.
 * Unknown values pass through as their raw string with no age range.
 */
export function formatClassLevel(level: string | null | undefined): ClassLevelDisplay {
  if (!level) return { label: '—', ages: null };
  return LEVEL_DISPLAY[level] ?? { label: level, ages: null };
}
